// Rung 3 of the disclosure ladder (DETAIL_LEVELS.md), on the number a manager
// is most likely to doubt: a projection.
//
// It exists at EVERY detail level. The level controls the face of a row; it
// never decides whether a number can explain itself. What the popover shows is
// where the number came from — season average, the pro opponent, the injury
// status that held it down — and nothing about what to do with it. That is the
// offer behind letting a manager decide how much to trust a prediction
// (docs/decisions/2026-09-12-tools-not-instructions.md).

import type { ReactNode } from 'react';
import type { PlayerSummary, ProjectionBasis } from '@fsp/domain';
import { Why, type WhyRow } from '../../kit/Why';
import { num } from './notComputed';

/**
 * The words a projection carries on its row, at EVERY detail level (N3): why
 * there is no number ("Not projected — no recent NFL games"), or why the number is
 * a stated 0 ("On bye", "Out", "Not on the active roster", "His team has
 * already played"). Null when the number speaks for itself. The server writes
 * the words; the client only decides where they sit. A matchup's nudge to a
 * real number ("Matchup −4%") is left to the popover.
 */
export function projectionNote(
  projection: number | null,
  basis: ProjectionBasis | null,
): string | null {
  if (basis === null) return null;
  if (projection === null) return basis.withheld;
  return projection === 0 ? basis.adjustment : null;
}

/**
 * A PROJECTION SAYS IT IS ONE (DIRECTION U4, B-295): the number with a small
 * "proj" under it. Every projection
 * printed where no heading names it goes through this; one under a Proj
 * column, or in a line that already says "proj", does not need it.
 */
export function Proj({ children }: { children: ReactNode }) {
  return (
    <span className="fsp-proj">
      {children}
      <span className="fsp-proj-tag">proj</span>
    </span>
  );
}

/**
 * A TRADE'S WORTH SAYS IT IS ONE (B-185): his points a week over the rest of
 * the season, his club's bye and opponents counted, with a small "a wk" under
 * it, as Proj says "proj". It is what
 * the read-out's first sentence sums, so the rows and the sentence agree.
 */
export function Worth({ children }: { children: ReactNode }) {
  return (
    <span className="fsp-proj">
      {children}
      <span className="fsp-proj-tag">a wk</span>
    </span>
  );
}

/**
 * The basis as popover rows. Facts in the order a manager would ask for them.
 *
 * `adjustment` holds two kinds of thing, told apart by the number beside it:
 * beside a stated 0 it is why he cannot score (*"Out"*, *"On bye"*) — his
 * status; beside a number it is what moved it (*"Matchup −4%"*, the demo's
 * *"Questionable"*). It was labeled "Status" either way, so a fixture's
 * nudge read as a player's status (SCREENS-7).
 */
export function basisRows(basis: ProjectionBasis, projection: number | null = null): WhyRow[] {
  const rows: WhyRow[] = [
    {
      // Before his first game there is nothing to average, and the popover
      // says so rather than showing a 0.0 beside a projection of 14.
      label: 'Season average',
      value: basis.seasonAvg === null ? '—' : basis.seasonAvg.toFixed(1),
      note: basis.seasonAvg === null ? 'no games yet' : `over ${basis.periods}`,
    },
  ];
  if (basis.opponent) rows.push({ label: 'This week', value: basis.opponent });
  if (basis.adjustment)
    rows.push({ label: projection === 0 ? 'Status' : 'Adjusted for', value: basis.adjustment });
  if (basis.withheld) rows.push({ label: 'Projection', value: basis.withheld });
  return rows;
}

export function ProjectionWhy({
  player,
  projection,
  basis,
  children,
}: {
  player: PlayerSummary | null;
  /** Null means nobody has projected this player. The Why sheet says so rather than showing a zero. */
  projection: number | null;
  basis: ProjectionBasis | null;
  children: ReactNode;
}) {
  // An empty slot has nothing to explain; render the number plainly rather
  // than an affordance that opens an empty box.
  if (!player || !basis) return <>{children}</>;
  return (
    <Why
      subject={player.name}
      value={num(projection)}
      rows={basisRows(basis, projection)}
      // Not "from this season's scoring": a projection leans on last season's
      // games while this one has few, and a defense's on the league's line.
      footnote="A projection in this league's scoring. Nobody knows the future."
    >
      {children}
    </Why>
  );
}
